import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';

import { usePropertyStore } from '@/stores/property-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { getWeatherForProperty } from '@/services/weather-service';
import {
  hpaToInHg,
  windDegToCardinal,
  type WeatherData,
  type PressureTrend,
} from '@/services/weather-service';
import { getSunTimes, getMoonData, getMoonPhaseEmoji } from '@/services/celestial-service';
import { scorePressure, scoreWind } from '@/services/conditions-quality-service';
import { posthogService } from '@/services/posthog-service';
import { EVENT_FORECAST_PAGE_VIEWED } from '@/services/observability/events';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import { ForecastDayRow } from '@/components/forecast/ForecastDayRow';
import type { ForecastDayRowData } from '@/components/forecast/ForecastDayRow';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDayLabel(dtUnix: number): string {
  const date = new Date(dtUnix * 1000);
  const day = date.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase().slice(0, 3);
  const dayNum = date.getDate();
  return `${day} ${dayNum}`;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function getDailyPressureTrend(days: WeatherData['daily'], index: number): PressureTrend {
  if (index === 0) return 'steady';
  const delta = days[index].pressure - days[index - 1].pressure;
  if (delta >= 1) return 'rising';
  if (delta <= -1) return 'falling';
  return 'steady';
}

function computeDailyScore(
  day: WeatherData['daily'][number],
  pressureTrend: PressureTrend,
): number {
  const pressureInHg = hpaToInHg(day.pressure);
  const pScore = scorePressure(pressureInHg, pressureTrend).score;
  const wScore = scoreWind(day.windSpeed).score;
  const rainFactor = Math.max(0, Math.round((1 - day.pop) * 100));
  return Math.round(pScore * 0.4 + wScore * 0.35 + rainFactor * 0.25);
}

function isColdFront(days: WeatherData['daily'], index: number): boolean {
  if (index === 0) return false;
  return days[index - 1].tempMax - days[index].tempMax >= 10;
}

function getHoursForDay(
  hourly: WeatherData['hourly'],
  dayDt: number,
): WeatherData['hourly'] {
  const dayStr = new Date(dayDt * 1000).toDateString();
  return hourly.filter((h) => new Date(h.dt * 1000).toDateString() === dayStr);
}

function computeHourlyScore(
  hour: WeatherData['hourly'][number],
  civilDawn: Date,
  civilDusk: Date,
): number {
  const pressureInHg = hpaToInHg(hour.pressure);
  const pScore = scorePressure(pressureInHg, 'steady').score;
  const wScore = scoreWind(hour.windSpeed).score;

  const nowMs = hour.dt * 1000;
  const minsFromDawn = Math.abs(nowMs - civilDawn.getTime()) / 60000;
  const minsFromDusk = Math.abs(nowMs - civilDusk.getTime()) / 60000;
  const closestMins = Math.min(minsFromDawn, minsFromDusk);
  let crepScore: number;
  if (closestMins <= 30) crepScore = 100;
  else if (closestMins <= 60) crepScore = 85;
  else if (closestMins <= 120) crepScore = 60;
  else crepScore = 20;

  return Math.round(pScore * 0.33 + wScore * 0.27 + crepScore * 0.40);
}

function trendArrow(trend: PressureTrend): string {
  if (trend === 'rising') return '↑';
  if (trend === 'falling') return '↓';
  return '→';
}

// ---------------------------------------------------------------------------
// Helper: build ForecastDayRowData from raw weather day + context
// ---------------------------------------------------------------------------

function buildDayRowData(
  day: WeatherData['daily'][number],
  index: number,
  allDays: WeatherData['daily'],
  lat: number,
  lng: number,
): ForecastDayRowData {
  const pressureTrend = getDailyPressureTrend(allDays, index);
  const score = computeDailyScore(day, pressureTrend);
  const cold = isColdFront(allDays, index);
  const moonData = getMoonData(new Date(day.dt * 1000), lat, lng);
  const moonEmoji = getMoonPhaseEmoji(moonData.phase);
  const sunTimes = getSunTimes(new Date(day.dt * 1000), lat, lng);
  const pressureInHg = hpaToInHg(day.pressure);
  const windDir = windDegToCardinal(day.windDeg);
  const label = formatDayLabel(day.dt);
  const trendStr = trendArrow(pressureTrend);

  const precipitationProbability = Math.round((day.pop ?? 0) * 100);
  const precipitationVolume =
    Math.round(((day.rain ?? 0) + (day.snow ?? 0)) / 25.4 * 100) / 100;

  return {
    dt: day.dt,
    label,
    score,
    tempMax: day.tempMax,
    tempMin: day.tempMin,
    windSpeed: day.windSpeed,
    windDir,
    moonEmoji,
    pressureInHg,
    pressureTrend: trendStr,
    civilDawnTime: formatTime(sunTimes.civilDawn),
    civilDuskTime: formatTime(sunTimes.civilDusk),
    isColdFront: cold,
    precipitationProbability,
    precipitationVolume,
  };
}

// ---------------------------------------------------------------------------
// ForecastPage
// ---------------------------------------------------------------------------

export function ForecastPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { properties, loadProperties } = usePropertyStore();
  const isPro = useUserProfileStore((s) => s.isPro);

  const [selectedId, setSelectedId] = useState<string>(() => searchParams.get('propertyId') ?? '');
  const [rawWeather, setRawWeather] = useState<WeatherData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  // Track forecast page view once on mount — only for Pro users (Warning #8)
  useEffect(() => {
    if (isPro()) {
      posthogService.track(EVENT_FORECAST_PAGE_VIEWED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (properties.length === 0) {
      void loadProperties();
    }
  }, [properties.length, loadProperties]);

  // Default to first property once loaded
  useEffect(() => {
    if (!selectedId && properties.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
      setSelectedId(properties[0].id);
    }
  }, [selectedId, properties]);

  const property = properties.find((p) => p.id === selectedId) ?? properties[0];

  useEffect(() => {
    if (!property) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loading flag set before async fetch; reset in cleanup
    setIsLoading(true);
    getWeatherForProperty(property.id, property.latitude, property.longitude)
      .then(setRawWeather)
      .catch((err) => {
        console.error('[ForecastPage] Failed to load weather:', err);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `property` is recomputed each render via properties.find(); depending on the object would refetch every render. Primitive id/lat/long deps are the intended trigger set.
  }, [property?.id, property?.latitude, property?.longitude]);

  // Gate: Starter users cannot access the 8-day forecast
  if (!isPro()) {
    return (
      <UpsellSheet
        featureName={UPSELL_COPY.eight_day_forecast.featureName}
        description={UPSELL_COPY.eight_day_forecast.description}
        featureGate="eight_day_forecast"
        onDismiss={() => navigate(-1)}
      />
    );
  }

  function handleToggle(index: number) {
    setExpandedIndex((prev) => (prev === index ? null : index));
  }

  // Build segmented control options for property selector
  const segmentedOptions = properties.map((p) => ({ value: p.id, label: p.name }));

  return (
    <div className="flex flex-col gap-4">
      {/* Back link — "< DASHBOARD" */}
      <Link
        to="/"
        className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
        style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        &lt; DASHBOARD
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-3">
        <h1 className="font-display text-display-lg text-[var(--color-text-primary)]">
          8-Day Forecast
        </h1>
        {property && (
          <p className="text-body-sm text-[var(--color-text-muted)]">{property.name}</p>
        )}

        {/* Property selector — hide if only 1 property */}
        {properties.length >= 2 && (
          <SegmentedControl
            options={segmentedOptions}
            value={selectedId}
            onChange={(id) => {
              setSelectedId(id);
              setRawWeather(null);
              setExpandedIndex(0);
            }}
          />
        )}
      </div>

      {/* Forecast list */}
      {isLoading && !rawWeather && (
        <div
          className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg p-4 animate-pulse"
          data-testid="forecast-skeleton"
        >
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 bg-[var(--color-border)] rounded" />
            ))}
          </div>
        </div>
      )}

      {!isLoading && !rawWeather && (
        <div
          className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg p-6 text-center"
          data-testid="forecast-unavailable"
        >
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Forecast unavailable — no weather connection
          </p>
        </div>
      )}

      {rawWeather && property && (
        <div
          className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg overflow-hidden"
          data-testid="forecast-list"
        >
          {rawWeather.daily.slice(0, 8).map((day, index) => {
            const dayHours = getHoursForDay(rawWeather.hourly, day.dt);
            const sunTimes = getSunTimes(new Date(day.dt * 1000), property.latitude, property.longitude);
            const activityScores =
              dayHours.length >= 3
                ? dayHours.map((h) => computeHourlyScore(h, sunTimes.civilDawn, sunTimes.civilDusk))
                : [];

            return (
              <ForecastDayRow
                key={day.dt}
                data={buildDayRowData(day, index, rawWeather.daily, property.latitude, property.longitude)}
                index={index}
                isExpanded={expandedIndex === index}
                onToggle={() => handleToggle(index)}
                activityScores={activityScores}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
